import {
  CheckCircle2, Clock, XCircle, MessageCircle,
  Mail, Phone, MapPin, User, Calendar, NotebookPen, Save,
} from "lucide-react";

// Internal pill — duplicated lightly here so AdminBookingRow stays self-contained.
const Pill = ({ tone = "zinc", children, testid }) => {
  const tones = {
    yellow: "bg-brand-yellow/15 text-brand-yellow border-brand-yellow/40",
    green:  "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    red:    "bg-red-500/15 text-red-300 border-red-500/40",
    purple: "bg-brand-purpleLight/15 text-brand-purpleLight border-brand-purpleLight/40",
    zinc:   "bg-white/5 text-zinc-300 border-white/15",
  };
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[9px] font-bold uppercase tracking-[0.2em] ${tones[tone] || tones.zinc}`}>
      {children}
    </span>
  );
};

const STATUS_TONE = { paid: "green", complete: "green", pending: "yellow", open: "yellow", failed: "red", expired: "red" };

const Field = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{label}</div>
    <div className="text-sm text-white mt-0.5">{children}</div>
  </div>
);

const ATTENDANCE_OPTIONS = [
  { v: "", label: "—" },
  { v: "attended", label: "Attended" },
  { v: "no-show", label: "No-show" },
  { v: "cancelled", label: "Cancelled" },
  { v: "rescheduled", label: "Rescheduled" },
];

export const AdminBookingRow = ({ it, editing, setEditing, savingId, onSave }) => {
  const b = it.booking || {};
  const sid = it.session_id;
  const editingRow = editing[sid] || {};
  const isEditingNotes = editingRow.attendance_notes !== undefined;
  const isEditingStatus = editingRow.attendance_status !== undefined;
  const noteVal = isEditingNotes ? editingRow.attendance_notes : (it.attendance_notes || "");
  const statusTone = STATUS_TONE[it.payment_status] || "zinc";

  const patchEditing = (patch) =>
    setEditing((prev) => ({ ...prev, [sid]: { ...(prev[sid] || {}), ...patch } }));

  return (
    <div
      data-testid={`admin-booking-row-${sid}`}
      className="rounded-2xl border border-white/10 bg-white/[0.03] hover:border-white/20 transition-colors p-5"
    >
      {/* Top row: identity + status */}
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <User size={14} className="text-brand-yellow" />
            <span className="font-display text-lg text-white tracking-wide truncate">
              {b.student_name || "—"}
            </span>
            {b.guardian_name && (
              <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                · Guardian: <span className="text-white">{b.guardian_name}</span>
              </span>
            )}
            {b.age_group && <Pill tone="purple">Age {b.age_group}</Pill>}
          </div>
          <div className="mt-1 text-xs text-zinc-400 flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="inline-flex items-center gap-1"><Mail size={11} className="text-zinc-500" /> {b.email || "—"}</span>
            <span className="inline-flex items-center gap-1"><Phone size={11} className="text-zinc-500" /> {b.phone || "—"}</span>
            {b.whatsapp_optin ? (
              <Pill tone="green" testid={`admin-booking-wa-${sid}`}>
                <MessageCircle size={9} /> WhatsApp OK
              </Pill>
            ) : (
              <Pill tone="zinc">WhatsApp: opt-out</Pill>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-2">
          <Pill tone={statusTone}>
            {it.payment_status === "paid" ? <CheckCircle2 size={9} /> : it.payment_status === "failed" || it.payment_status === "expired" ? <XCircle size={9} /> : <Clock size={9} />}
            {it.payment_status || "pending"}
          </Pill>
          {it.attendance_status && (
            <Pill tone={it.attendance_status === "attended" ? "green" : it.attendance_status === "no-show" ? "red" : "purple"}>
              {it.attendance_status}
            </Pill>
          )}
          <span className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">
            {((it.metadata || {}).provider || "stripe").toUpperCase()} · £{Number(it.amount || 0).toFixed(2)}
          </span>
        </div>
      </div>

      {/* Booking details grid */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-white/10">
        <Field label="Class booked">{b.class_type || (it.metadata || {}).title || "—"}</Field>
        <Field label="Location"><span className="inline-flex items-center gap-1"><MapPin size={11} className="text-brand-yellow" /> {b.location || "—"}</span></Field>
        <Field label="Day / Time">
          <span className="inline-flex items-center gap-1.5">
            <Calendar size={11} className="text-brand-yellow" />
            {(b.preferred_day || "—")} {b.preferred_time ? `· ${b.preferred_time}` : ""}
          </span>
        </Field>
        <Field label="Booked on">{(it.created_at || "").slice(0, 16).replace("T", " ")}</Field>
      </div>

      {b.notes && (
        <div className="mt-3 text-xs text-zinc-300 bg-white/[0.03] border border-white/10 rounded-xl p-3">
          <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mr-2">Student notes</span>
          {b.notes}
        </div>
      )}

      {b.utm && (
        <div className="mt-3 text-[11px] text-zinc-400 bg-brand-purpleDeep/15 border border-brand-purpleLight/25 rounded-xl p-3 flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="text-[9px] uppercase tracking-[0.25em] text-brand-purpleLight font-bold">Attribution</span>
          {b.utm.utm_source && <span><strong className="text-white">source:</strong> {b.utm.utm_source}</span>}
          {b.utm.utm_medium && <span><strong className="text-white">medium:</strong> {b.utm.utm_medium}</span>}
          {b.utm.utm_campaign && <span><strong className="text-white">campaign:</strong> {b.utm.utm_campaign}</span>}
          {b.utm.utm_content && <span><strong className="text-white">content:</strong> {b.utm.utm_content}</span>}
          {b.utm.ref && <span><strong className="text-white">ref:</strong> {b.utm.ref}</span>}
          {b.utm.focus && <span><strong className="text-white">focus:</strong> {b.utm.focus}</span>}
        </div>
      )}

      {/* Attendance notes editor */}
      <div className="mt-4 pt-4 border-t border-white/10">
        <div className="flex items-center gap-3 mb-2 flex-wrap">
          <div className="flex items-center gap-2">
            <NotebookPen size={12} className="text-brand-yellow" />
            <span className="text-[10px] uppercase tracking-[0.25em] text-brand-yellow font-bold">Attendance</span>
          </div>
          <select
            data-testid={`admin-booking-attendance-status-${sid}`}
            value={(editingRow.attendance_status ?? it.attendance_status ?? "")}
            onChange={(e) => patchEditing({ attendance_status: e.target.value })}
            className="ad-input !py-1.5 !w-auto text-xs"
          >
            {ATTENDANCE_OPTIONS.map((o) => (
              <option key={o.v} value={o.v}>{o.label}</option>
            ))}
          </select>
        </div>
        <textarea
          data-testid={`admin-booking-notes-${sid}`}
          className="ad-input min-h-[64px] resize-none"
          value={noteVal}
          onChange={(e) => patchEditing({ attendance_notes: e.target.value })}
          placeholder="Add internal notes — e.g. attendance details, follow-up actions, payment chase status…"
        />
        <div className="mt-2 flex items-center justify-end gap-2">
          {(isEditingNotes || isEditingStatus) && (
            <button
              onClick={() => setEditing((prev) => ({ ...prev, [sid]: undefined }))}
              className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 hover:text-white"
            >
              Cancel
            </button>
          )}
          <button
            onClick={() => onSave(it)}
            data-testid={`admin-booking-save-${sid}`}
            disabled={savingId === sid || (!isEditingNotes && !isEditingStatus)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-yellow text-black text-[10px] font-black uppercase tracking-[0.2em] hover:bg-brand-yellowHover disabled:opacity-50"
          >
            <Save size={11} /> Save
          </button>
        </div>
      </div>
    </div>
  );
};
